import { COLOR_CLINICS, COLOR_PRIMARY } from '../../routes/color-constants'
import { Field } from '../../components/table'
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Actions, Pager, Sort } from '../../components/table/types'
import { useTranslation } from 'react-i18next'
import { Query, SortParam } from '../../common/api/Query'
import downloadIcon from '../../assets/table-icons/download-icon.svg'
import deleteIcon from '../../assets/table-icons/delete-icon.svg'
import { Box, Button, CircularProgress, Modal } from '@material-ui/core'
import { useStyles } from '../matchings/Matching.styles'
import { FormAction, FormActions } from '../../common/utils/form-generation'
import { commonStyles } from '../../common/styles/Styles'
import { getFileContainer } from 'container/file-module'
import { FileService } from 'modules/files/services/FileService'
import { FILE_SERVICE_KEY } from 'modules/files'
import { Kit } from 'modules/files/models/File'
import { useSnackbar } from 'notistack'
import { v4 as uuidv4 } from 'uuid'
import { getAuthContainer } from 'container/auth-modules'
import { AuthService } from 'modules/auth/services/AuthService'
import { AUTH_SERVICE_KEY } from 'modules/auth'
import { Pagination } from 'components/table/Pagination'
import { Table, TableContainer } from '@material-ui/core'
import { Head } from 'components/table/Head'
import { TableCell, TableRow, TextField } from '@material-ui/core'
import { GenericTooltip } from 'components/generic-tooltip'
import { saveAs } from 'file-saver'

const fileService = getFileContainer().get<FileService>(FILE_SERVICE_KEY)
const authService = getAuthContainer().get<AuthService>(AUTH_SERVICE_KEY)

const ALLOWED_EXTENSIONS = ['zip', 'gz', 'tgz', 'tar', 'bz2', 'xz', '7z', 'rar']

// <codigo>_<descripcion>.<extension>, p.ej. K1_PGDSeq_v1.zip
// El código es libre (lo introduce el usuario), por eso se admiten letras,
// números, punto, guion y guion bajo, y no puede contener espacios.
const KIT_FILE_FORMAT = /^([a-zA-Z0-9][a-zA-Z0-9-]{0,63})_(.+)\.(zip|gz|tgz|tar|bz2|xz|7z|rar)$/i

const extensionOf = (fileName: string): string => {
    const parts = fileName.split('.')
    if (parts.length < 2) return ''
    return parts.pop()!.toLowerCase()
}

const formatSize = (bytes: number): string => {
    if (!bytes) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB']
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`
}

export const Kits = () => {
    const { t } = useTranslation()
    const classes = useStyles({ color: COLOR_PRIMARY })
    const styles = commonStyles()
    const { enqueueSnackbar } = useSnackbar()
    const loggedUser = authService.get()

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isUploading, setIsUploading] = useState<boolean>(false)
    const [open, setOpen] = useState<boolean>(false)
    const [pager, setPager] = useState<Pager>()
    const [page, setPage] = useState<number>(0)
    const [itemsPerPage, setItemsPerPage] = useState<number>(10)
    const [allItems, setAllItems] = useState<Kit[]>([])
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [sort, setSort] = useState<SortParam<Kit>>({ field: 'date', desc: true })
    const [currentKit, setCurrentKit] = useState<Kit>()
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const savedCodes = useRef<Record<string, string>>({})


    const loadKits = useCallback(() => {
        setIsLoading(true)
        fileService
            .getKits(new Query({ sort: [{ field: sort.field, desc: sort.desc }] }))
            .subscribe((res) => {
                setAllItems(res.items)
                // Código persistido en servidor, para no enviar un PUT si el
                // usuario sólo puso el foco en el campo y no lo modificó.
                savedCodes.current = res.items.reduce<Record<string, string>>((acc, kit) => {
                    acc[kit.id] = kit.code
                    return acc
                }, {})
                setIsLoading(false)
            })
    }, [sort.field, sort.desc])

    useEffect(() => {
        loadKits()
    }, [loadKits])

    const filteredItems = allItems.filter(
        (item) =>
            item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.zipName.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const startIndex = page * itemsPerPage
    const pagedFilteredItems = filteredItems.slice(startIndex, startIndex + itemsPerPage)

    useEffect(() => {
        setPager({
            page,
            count: filteredItems.length,
            handleChangePage: (_event: unknown, value: number) => setPage(value),
            rowsPerPage: itemsPerPage,
            handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => {
                const newRowsPerPage = Number.parseInt(event.target.value)
                setItemsPerPage(Number.isNaN(newRowsPerPage) || newRowsPerPage <= 0 ? 10 : newRowsPerPage)
                setPage(0)
            },
        })
    }, [page, filteredItems.length, itemsPerPage])

    const toFormData = (file: File): FormData => {
        const formData = new FormData()
        formData.append('data', file, file.name)
        formData.append('id', uuidv4())
        formData.append('name', file.name)
        formData.append('size', file.size.toString())
        formData.append('extension', extensionOf(file.name))
        formData.append('ownerID', loggedUser.id)
        return formData
    }

    const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.target as HTMLInputElement
        const files = Array.from(input.files || [])

        if (files.length === 0) return

        const rejectedByExtension = files.filter((f) => !ALLOWED_EXTENSIONS.includes(extensionOf(f.name)))
        const accepted = files.filter(
            (f) => ALLOWED_EXTENSIONS.includes(extensionOf(f.name)) && KIT_FILE_FORMAT.test(f.name)
        )

        if (rejectedByExtension.length > 0) {
            enqueueSnackbar(t('kitMustHaveCompressedExtension'), { variant: 'error' })
        }

        const rejectedByFormat = files.filter(
            (f) => ALLOWED_EXTENSIONS.includes(extensionOf(f.name)) && !KIT_FILE_FORMAT.test(f.name)
        )

        if (rejectedByFormat.length > 0) {
            enqueueSnackbar(t('kitMustHaveFormat'), { variant: 'error' })
        }

        if (accepted.length === 0) {
            input.value = ''
            return
        }

        setIsUploading(true)

        const results = await Promise.all(accepted.map((file) => fileService.uploadKit(toFormData(file))))

        const failed = results.filter((r) => r === undefined).length
        const succeeded = results.length - failed

        if (failed > 0) {
            enqueueSnackbar(t('kitsUploadError', { count: failed }), { variant: 'error' })
        }

        if (succeeded > 0) {
            enqueueSnackbar(t('kitsUploadSuccess', { count: succeeded }), { variant: 'success' })
        }

        setIsUploading(false)
        input.value = ''
        loadKits()
    }

    const changeFile = () => fileInputRef.current?.click()

    const handleChangeCode = (kitId: string, code: string) => {
        // Se localiza por id: el índice de la fila viene de la lista filtrada/paginada,
        // que no coincide con el índice en allItems.
        setAllItems((prev) =>
            prev.map((k) => (k.id === kitId ? new Kit({ ...k.toDTO(), code }) : k))
        )
    }

    const handleUpdate = (kitId: string) => {
        const kit = allItems.find((k) => k.id === kitId)
        if (!kit) return

        const code = kit.code.trim()

        // Sólo persistir si el valor cambió respecto a lo que hay en el servidor.
        if (code === savedCodes.current[kit.id]) return

        if (code === '') {
            enqueueSnackbar(t('kitsUpdateError'), { variant: 'error' })
            loadKits()
            return
        }

        fileService.updateKit({ ...kit.toDTO(), code }).subscribe((updated) => {
            if (!updated) {
                enqueueSnackbar(t('kitsUpdateError'), { variant: 'error' })
                return
            }
            loadKits()
        })
    }

    const selectKit = (k: Kit) => {
        setCurrentKit(k)
        setOpen(true)
    }

    const deleteKit = () => {
        fileService.deleteKit(currentKit?.id || '').subscribe(() => loadKits())
    }

    const handleDelete = () => {
        setOpen(false)
        deleteKit()
    }

    const closeModal = () => {
        setOpen(false)
        setCurrentKit(undefined)
    }

    const actionsDelete: FormAction[] = [{ label: t('close'), handleAction: closeModal }]

    const downloadKit = (item: Kit) => {
        fileService.downloadKit(item.id).then((res) => {
            if (!res) return
            saveAs(res.blob, item.zipName)
        })
    }

    const actions: Actions<Kit> = {
        actionsColumn: t('Actions'),
        items: [
            { handler: downloadKit, icon: downloadIcon, label: t('download2') },
            { handler: selectKit, icon: deleteIcon, label: t('delete') },
        ],
    }

    const sortable: Sort<Kit> = {
        name: sort.field,
        direction: sort.desc ? 'desc' : 'asc',
        handleSort: (field) => {
            setSort({ field, desc: sort.field === field ? !sort.desc : true })
        },
    }

    const fields: Field<Kit>[] = [
        { sortable: true, searchable: true, label: t('code'), name: 'code' },
        { sortable: true, searchable: true, label: t('fileName'), name: 'zipName' },
        { sortable: true, searchable: false, label: t('date'), name: 'date' },
        { sortable: false, searchable: false, label: t('size'), name: 'size' },
    ]

    return (
        <>
            <Box justifyContent={'space-between'} mb={2}>
                <Button
                    className={classes.button}
                    variant={'contained'}
                    onClick={changeFile}
                    disabled={isUploading}
                    style={{ float: 'right' }}>
                    {t('uploadKits')}
                </Button>
            </Box>
            <Box mb={2}>
                <TextField
                    label={t('search')}
                    variant="outlined"
                    style={{ float: 'left' }}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                />
            </Box>

            {isLoading ? (
                <Box style={{ width: '100%', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer>
                    <Table>
                        <Head fields={fields} sort={sortable} actions={actions} styleHeader={{ color: COLOR_CLINICS }} />
                        {pagedFilteredItems.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>
                                    <TextField
                                        fullWidth
                                        id="code"
                                        type={'text'}
                                        label={t('code')}
                                        value={item.code}
                                        onChange={(e) => handleChangeCode(item.id, e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        onBlur={() => handleUpdate(item.id)}
                                    />
                                </TableCell>
                                <TableCell>{item.zipName}</TableCell>
                                <TableCell>{item.date ? new Date(item.date).toLocaleString() : ''}</TableCell>
                                <TableCell>{formatSize(item.size)}</TableCell>

                                {actions && actions.items?.length > 0 && (
                                    <TableCell key="actions-table">
                                        <Box display="flex" justifyContent="space-between">
                                            {actions.items.map((a) => {
                                                if (a.hidden && a.hidden(item)) {
                                                    return <></>
                                                }
                                                return (
                                                    <GenericTooltip
                                                        key={a.label || a.icon}
                                                        values={[t(a.label || a.icon)]}
                                                        icon={
                                                            <img
                                                                src={a.icon}
                                                                alt={a.label || ''}
                                                                className={classes.icon}
                                                                onClick={() => a.handler(item)}
                                                            />
                                                        }
                                                        noUseFab={true}
                                                    />
                                                )
                                            })}
                                        </Box>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </Table>
                </TableContainer>
            )}

            {pager && <Pagination {...pager} />}

            <Modal open={open}>
                <Box className={styles.modal}>
                    <form onSubmit={handleDelete}>
                        <Box mt={2} textAlign={'justify'}>
                            {t('kitConfirmationMessage')}
                        </Box>
                        <FormActions actions={actionsDelete} message={t('confirm')} />
                    </form>
                </Box>
            </Modal>

            <input onChange={handleUpload} multiple ref={fileInputRef} type="file" hidden />
        </>
    )
}
